/**
 * Template Preview Component
 *
 * Shows a live preview of email templates with example variable substitution.
 * Renders HTML in the preview for fidelity.
 */

import { Alert, Box, Divider, Paper, ScrollArea, Stack, Text } from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import type { EmailEventType } from "../../../../../../../types/email-event.d";
import { EMAIL_EVENT_METADATA } from "../../email-event-metadata";

/**
 * Props for TemplatePreview
 */
export interface TemplatePreviewProperties {
  /** Event type for variable context */
  eventType: EmailEventType;
  /** Subject template to preview */
  subjectTemplate: string;
  /** Body template to preview */
  bodyTemplate: string;
}

/**
 * Replace template variables with example values
 * @param template
 * @param eventType
 */
function renderTemplateWithExamples(template: string, eventType: EmailEventType): string {
  const metadata = EMAIL_EVENT_METADATA[eventType];
  if (!metadata) return template;

  let rendered = template;

  // Replace each variable with its example value
  for (const variable of metadata.available_variables) {
    const regex = new RegExp(`\\{\\{${variable.name}\\}\\}`, "g");
    rendered = rendered.replace(regex, variable.example);
  }

  return rendered;
}

/**
 * TemplatePreview Component
 * @param root0
 * @param root0.eventType
 * @param root0.subjectTemplate
 * @param root0.bodyTemplate
 * @example
 * <TemplatePreview
 *   eventType="ideia-comentario-mention"
 *   subjectTemplate="Você foi mencionado | {{ideia.titulo}}"
 *   bodyTemplate="<p>Olá {{colaborador.nome}}</p>"
 * />
 */
export function TemplatePreview({ eventType, subjectTemplate, bodyTemplate }: TemplatePreviewProperties) {
  const renderedSubject = renderTemplateWithExamples(subjectTemplate, eventType);
  const renderedBody = renderTemplateWithExamples(bodyTemplate, eventType);

  // const metadata = EMAIL_EVENT_METADATA[eventType];

  return (
    <Stack gap="md">
      <Alert icon={<IconInfoCircle size={16} />} title="Preview com dados de exemplo" color="primary" variant="light">
        <Text size="sm">As variáveis foram substituídas com valores de exemplo para visualização.</Text>
      </Alert>

      {/* Subject Preview */}
      <Paper withBorder p="md">
        <Stack gap="xs">
          <Text size="sm" fw={500} c="dimmed">
            Assunto
          </Text>
          <Text size="sm" fw={600}>
            {renderedSubject || (
              <Text c="dimmed" fs="italic">
                Sem assunto
              </Text>
            )}
          </Text>
        </Stack>
      </Paper>

      {/* Body Preview */}
      <Paper withBorder p="md">
        <Stack gap="xs">
          <Text size="sm" fw={500} c="dimmed">
            Corpo do Email (HTML Renderizado)
          </Text>
          <Divider />
          {bodyTemplate ? (
            <ScrollArea h={200}>
              <Box style={{ fontSize: "12px", lineHeight: 1.4 }} dangerouslySetInnerHTML={{ __html: renderedBody }} />
            </ScrollArea>
          ) : (
            <Text c="dimmed" fs="italic" size="sm">
              Sem conteúdo
            </Text>
          )}
        </Stack>
      </Paper>
    </Stack>
  );
}

TemplatePreview.displayName = "TemplatePreview";
